import { computed } from 'vue';
import { useUserStore } from '@/store/userStore';

/**
 * Composable para facilitar verificação de permissões
 * 
 * @returns {Object} Métodos para verificar permissões
 */
export function usePermissions() {
  const userStore = useUserStore();

  /**
   * Verifica se usuário é admin
   */
  const isAdmin = computed(() => userStore.isAdmin);

  /**
   * Verifica se usuário tem acesso a uma aplicação
   * @param {string} appName - Nome da aplicação
   * @returns {boolean}
   */
  const hasAccess = (appName) => {
    // IMPORTANTE: Admin também precisa ter permissão configurada
    return userStore.hasAccess(appName);
  };

  /**
   * Verifica se usuário tem permissão específica
   * @param {string} appName - Nome da aplicação
   * @param {string} permission - Tipo: access, insert, update, delete, export, print
   * @returns {boolean}
   */
  const hasPermission = (appName, permission = 'access') => {
    // IMPORTANTE: Admin também precisa ter permissão configurada
    return userStore.hasPermission(appName, permission);
  };

  /**
   * Verifica permissão de INSERIR
   */
  const canInsert = (appName) => {
    return hasPermission(appName, 'insert');
  };

  /**
   * Verifica permissão de ATUALIZAR
   */
  const canUpdate = (appName) => {
    return hasPermission(appName, 'update');
  };

  /**
   * Verifica permissão de DELETAR
   */
  const canDelete = (appName) => {
    return hasPermission(appName, 'delete');
  };

  /**
   * Verifica permissão de EXPORTAR
   */
  const canExport = (appName) => {
    return hasPermission(appName, 'export');
  };

  /**
   * Verifica permissão de IMPRIMIR
   */
  const canPrint = (appName) => {
    return hasPermission(appName, 'print');
  };

  /**
   * Obtém todas as permissões de uma aplicação
   */
  const getAppPermissions = (appName) => {
    return userStore.getAppPermissions(appName);
  };

  /**
   * Verifica se usuário tem TODAS as permissões especificadas
   * @param {string} appName - Nome da aplicação
   * @param {Array} permissions - Array de permissões ['insert', 'update', 'delete']
   */
  const hasAllPermissions = (appName, permissions = []) => {
    // IMPORTANTE: Admin também precisa ter permissão configurada
    return permissions.every(perm => hasPermission(appName, perm));
  };

  /**
   * Verifica se usuário tem ALGUMA das permissões especificadas
   * @param {string} appName - Nome da aplicação
   * @param {Array} permissions - Array de permissões ['insert', 'update', 'delete']
   */
  const hasAnyPermission = (appName, permissions = []) => {
    // IMPORTANTE: Admin também precisa ter permissão configurada
    return permissions.some(perm => hasPermission(appName, perm));
  };

  return {
    // Estado
    isAdmin,
    
    // Métodos básicos
    hasAccess,
    hasPermission,
    
    // Métodos específicos
    canInsert,
    canUpdate,
    canDelete,
    canExport,
    canPrint,
    
    // Métodos avançados
    getAppPermissions,
    hasAllPermissions,
    hasAnyPermission
  };
}
